import React from "react";
import { Mail, Phone, MapPin, ArrowUpRight, LifeBuoy, Receipt } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Logo from "@/components/site/Logo";
import { TIDS } from "@/lib/testIds";
import { EMAILS, PHONE, PHONE_RAW } from "@/lib/seo";

export default function Footer() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const onLanding = pathname === "/";

  const goToSection = (hash) => {
    if (onLanding) {
      document.querySelector(hash)?.scrollIntoView({ behavior: "smooth" });
    } else {
      navigate(`/${hash}`);
    }
  };

  const sections = [
    { hash: "#services", label: "Services" },
    { hash: "#process", label: "Process" },
    { hash: "#results", label: "Results" },
    { hash: "#faq", label: "FAQ" },
  ];

  return (
    <footer className="relative border-t border-white/5 bg-[#050810]">
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-20">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <div className="mb-6">
              <Logo className="h-11" />
            </div>
            <p className="text-white/60 max-w-sm leading-relaxed">
              The undeniable marketing system for dentists. We build
              predictable new-patient flow, measured in booked appointments,
              not clicks.
            </p>

            <button
              onClick={() => goToSection("#booking")}
              className="mt-8 group inline-flex items-center gap-2 rounded-full bg-amber-400 text-[#070B14] px-5 h-11 font-medium hover:bg-amber-300 transition-colors duration-200"
              data-testid="footer-cta-book"
            >
              Book a free call
              <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-200" />
            </button>
          </div>

          <div className="md:col-span-3">
            <div className="font-mono-accent text-[10px] uppercase tracking-[0.2em] text-white/40 mb-5">
              Explore
            </div>
            <ul className="space-y-3 text-white/70 text-sm">
              {sections.map((s) => (
                <li key={s.hash}>
                  <button
                    onClick={() => goToSection(s.hash)}
                    className="hover:text-amber-400 transition-colors"
                  >
                    {s.label}
                  </button>
                </li>
              ))}
              <li>
                <Link
                  to="/blog"
                  data-testid="footer-link-blog"
                  className="hover:text-amber-400 transition-colors"
                >
                  Blog
                </Link>
              </li>
            </ul>
          </div>

          <div className="md:col-span-4">
            <div className="font-mono-accent text-[10px] uppercase tracking-[0.2em] text-white/40 mb-5">
              Contact
            </div>
            <ul className="space-y-4 text-white/70 text-sm">
              <li className="flex items-start gap-3">
                <Mail className="w-4 h-4 mt-0.5 text-amber-400" />
                <span>
                  <a
                    href={`mailto:${EMAILS.hello}`}
                    data-testid={TIDS.footerContactLink}
                    className="hover:text-white transition-colors"
                  >
                    {EMAILS.hello}
                  </a>
                  <span className="block text-xs text-white/40 mt-0.5">
                    New enquiries and strategy calls
                  </span>
                </span>
              </li>
              <li className="flex items-start gap-3">
                <LifeBuoy className="w-4 h-4 mt-0.5 text-amber-400" />
                <span>
                  <a
                    href={`mailto:${EMAILS.support}`}
                    data-testid="footer-support-link"
                    className="hover:text-white transition-colors"
                  >
                    {EMAILS.support}
                  </a>
                  <span className="block text-xs text-white/40 mt-0.5">
                    Existing client support
                  </span>
                </span>
              </li>
              <li className="flex items-start gap-3">
                <Receipt className="w-4 h-4 mt-0.5 text-amber-400" />
                <span>
                  <a
                    href={`mailto:${EMAILS.billing}`}
                    data-testid="footer-billing-link"
                    className="hover:text-white transition-colors"
                  >
                    {EMAILS.billing}
                  </a>
                  <span className="block text-xs text-white/40 mt-0.5">
                    Invoices and accounts
                  </span>
                </span>
              </li>
              <li className="flex items-start gap-3">
                <Phone className="w-4 h-4 mt-0.5 text-amber-400" />
                <a href={`tel:${PHONE_RAW}`} className="hover:text-white transition-colors">
                  {PHONE}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="w-4 h-4 mt-0.5 text-amber-400" />
                <span>Remote-first · HQ in Mumbai, India</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-white/5 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="text-xs text-white/40 font-mono-accent uppercase tracking-[0.2em]">
            © {new Date().getFullYear()} Dental Leads Lab. All rights reserved.
          </div>
          <div className="flex items-center gap-6 text-xs text-white/40">
            <a
              href={`mailto:${EMAILS.info}`}
              className="hover:text-white transition-colors"
            >
              {EMAILS.info}
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
